import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from '@reduxjs/toolkit'
import {
  clearPasswordResetSession,
  clearSignupSetupSession,
} from '@/lib/signupSession'
import {
  authCompleteSignupProfile,
  authGoogle,
  authLogin,
  authLogout,
  changePassword,
  confirmPasswordReset,
  requestPasswordReset,
  type AuthRole,
  type ChangePasswordInput,
  type CompleteSignupProfilePayload,
  type ConfirmPasswordResetInput,
  type MockAuthResult,
  type MockUser,
  type RequestPasswordResetInput,
} from '@/services/authService'
import { PASSWORD_API_PATHS } from '@/services/authPasswordPaths'

export type { AuthRole }

export type AuthUser = MockUser

export interface AuthState {
  user: AuthUser | null
  role: AuthRole | null
  isAuthenticated: boolean
  loading: boolean
  error: string | null
}

const AUTH_TOKEN_KEY = 'authToken'
const AUTH_ROLE_KEY = 'authRole'
const AUTH_USER_KEY = 'authUser'
const AUTH_TIMESTAMP_KEY = 'tokenTimestamp'
/** `jwt` = Bearer in localStorage; `cookie` = HttpOnly session cookie (no JWT in frontend). Sync with `api.ts` AUTH_TRANSPORT_STORAGE_KEY. */
export const AUTH_TRANSPORT_KEY = 'launchpad_auth_transport'

export const AUTH_STORAGE_KEYS = {
  token: AUTH_TOKEN_KEY,
  role: AUTH_ROLE_KEY,
  user: AUTH_USER_KEY,
  tokenTimestamp: AUTH_TIMESTAMP_KEY,
  transport: AUTH_TRANSPORT_KEY,
} as const

const TOKEN_TTL_MS = 24 * 60 * 60 * 1000

export const isTokenExpired = (tokenTimestamp: number, now = Date.now()) =>
  now - tokenTimestamp > TOKEN_TTL_MS

const persistAuth = ({
  token,
  role,
  user,
}: Pick<MockAuthResult, 'token' | 'role' | 'user'>) => {
  if (typeof window === 'undefined') return
  const useCookieOnly = !token.trim()
  if (useCookieOnly) {
    localStorage.removeItem(AUTH_TOKEN_KEY)
    localStorage.setItem(AUTH_TRANSPORT_KEY, 'cookie')
  } else {
    localStorage.setItem(AUTH_TOKEN_KEY, token)
    localStorage.setItem(AUTH_TRANSPORT_KEY, 'jwt')
  }
  localStorage.setItem(AUTH_ROLE_KEY, role)
  localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user))
  localStorage.setItem(AUTH_TIMESTAMP_KEY, String(Date.now()))
}

const clearPersistedAuth = () => {
  if (typeof window === 'undefined') return
  localStorage.removeItem(AUTH_TOKEN_KEY)
  localStorage.removeItem(AUTH_ROLE_KEY)
  localStorage.removeItem(AUTH_USER_KEY)
  localStorage.removeItem(AUTH_TIMESTAMP_KEY)
  localStorage.removeItem(AUTH_TRANSPORT_KEY)
  // User-scoped caches — must not leak to the next account on shared browsers.
  localStorage.removeItem('soft-launch-validation-responses')
  localStorage.removeItem('soft-launch-validated-ideas')
  localStorage.removeItem('soft-launch-my-ideas')
  localStorage.removeItem('soft-launch-draft-ideas')
}

const initialState: AuthState = {
  user: null,
  role: null,
  isAuthenticated: false,
  loading: false,
  error: null,
}

export const signUpThunk = createAsyncThunk<
  MockAuthResult,
  CompleteSignupProfilePayload,
  { rejectValue: string }
>('auth/signUp', async (payload, { rejectWithValue }) => {
  try {
    return await authCompleteSignupProfile(payload)
  } catch (e) {
    const msg =
      e instanceof Error ? e.message : 'Signup failed. Please try again.'
    return rejectWithValue(msg)
  }
})

export const loginThunk = createAsyncThunk<
  MockAuthResult,
  { email: string; password: string }
>('auth/login', async ({ email, password }) => {
  return authLogin(email, password)
})

/**
 * Full logout: clears the backend HttpOnly cookie, then clears Redux +
 * localStorage + sessionStorage. Client state always ends up cleared even if
 * the network call fails (authLogout swallows errors).
 */
export const logoutThunk = createAsyncThunk<void, void>(
  'auth/logoutThunk',
  async (_, { dispatch }) => {
    await authLogout()
    dispatch(logout())
  },
)

export type GoogleAuthArg = { idToken: string } | { accessToken: string }

export const googleAuthThunk = createAsyncThunk<
  MockAuthResult,
  GoogleAuthArg,
  { rejectValue: string }
>('auth/google', async (arg, { rejectWithValue }) => {
  try {
    return await authGoogle(arg)
  } catch (e) {
    const msg =
      e instanceof Error
        ? e.message
        : 'Google sign-in failed. Please try again.'
    return rejectWithValue(msg)
  }
})

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<AuthUser | null>) => {
      state.user = action.payload
      state.isAuthenticated = action.payload !== null
    },
    /** Shallow-merge fields onto the current auth user (e.g. backfill `handle` after `GET /user/me`). */
    mergeUser: (state, action: PayloadAction<Partial<AuthUser>>) => {
      if (!state.user) return
      state.user = { ...state.user, ...action.payload }
      if (typeof window !== 'undefined') {
        try {
          localStorage.setItem(AUTH_USER_KEY, JSON.stringify(state.user))
        } catch {
          /* ignore quota / serialization errors */
        }
      }
    },
    setRole: (state, action: PayloadAction<AuthRole | null>) => {
      state.role = action.payload
    },
    logout: (state) => {
      state.user = null
      state.role = null
      state.isAuthenticated = false
      state.loading = false
      state.error = null
      clearPersistedAuth()
      clearSignupSetupSession()
      clearPasswordResetSession()
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signUpThunk.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(signUpThunk.fulfilled, (state, action) => {
        state.loading = false
        state.error = null
        persistAuth(action.payload)
        state.user = action.payload.user
        state.role = action.payload.role
        state.isAuthenticated = true
      })
      .addCase(signUpThunk.rejected, (state, action) => {
        state.loading = false
        state.error =
          typeof action.payload === 'string' ? action.payload : 'Signup failed'
        state.isAuthenticated = false
      })

      .addCase(loginThunk.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(loginThunk.fulfilled, (state, action) => {
        state.loading = false
        state.error = null
        persistAuth(action.payload)
        state.user = action.payload.user
        state.role = action.payload.role
        state.isAuthenticated = true
      })
      .addCase(loginThunk.rejected, (state) => {
        state.loading = false
        state.error = 'Login failed'
        state.isAuthenticated = false
      })

      .addCase(googleAuthThunk.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(googleAuthThunk.fulfilled, (state, action) => {
        state.loading = false
        state.error = null
        persistAuth(action.payload)
        state.user = action.payload.user
        state.role = action.payload.role
        state.isAuthenticated = true
      })
      .addCase(googleAuthThunk.rejected, (state, action) => {
        state.loading = false
        state.error =
          typeof action.payload === 'string'
            ? action.payload
            : 'Google sign-in failed'
        state.isAuthenticated = false
      })
  },
})

export const { setUser, mergeUser, setRole, logout, setLoading, setError } =
  authSlice.actions
export default authSlice.reducer

/** Password API — same as `@/services/authService`; import from here if you prefer the auth module. */
export {
  PASSWORD_API_PATHS,
  changePassword,
  confirmPasswordReset,
  requestPasswordReset,
  type ChangePasswordInput,
  type ConfirmPasswordResetInput,
  type RequestPasswordResetInput,
}
